import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { abbreviateAllVariants } from 'modules/abbreviate-all-variants';
import { thumbExtensionCreator } from 'modules/thumb-extension-creator';
import { ICONS_PRODUCT_HERO } from 'constants/icons';
import { Title } from 'components/product-title';
import { Tick } from 'components/icons';
import { LazyImage } from 'components/lazy-image';

import styles from './style.module.scss';
import {
  Description,
  DescriptionPart,
  LitOptions,
  LitOption,
} from './animation.style.js';

export const ProductHero = ({
  theme,
  background,
  title,
  litEffects,
  cutOut,
  allVariants,
  smallScreen,
  hasPatentProtection,
}) => {
  const [entered, setEntered] = useState(false);
  const varOptions = abbreviateAllVariants(allVariants);
  const variants = varOptions ? varOptions.join(' / ') : null;

  useEffect(() => {
    setEntered(true);
    // eslint-disable-next-line
  }, []);

  const iconClasses = classNames({
    [`${styles['lit-icon']}`]: true,
    [`${styles[theme]}`]: theme,
  });

  return (
    <div className={`${styles.hero} ${styles[theme]}`}>
      <div className={styles['background-wrapper']}>
        <div className={`${styles[theme]}`} /> {/* overlay */}
        <LazyImage
          thumbnailSrc={thumbExtensionCreator(background, '-thumb')}
          src={background}
          alt={`${title} product`}
        />
      </div>
      <div className="container">
        <div className={styles['content-wrapper']}>
          <Description
            className={styles['description-wrapper']}
            pose={entered ? 'enter' : 'exit'}
          >
            {!smallScreen && <Title title={title} theme={theme} />}
            <DescriptionPart>
              <p className={styles.variants}>{variants}</p>
            </DescriptionPart>
            <DescriptionPart>
              <p
                className={
                  hasPatentProtection
                    ? styles['cut'] + ' ' + styles['patent-protection-cut']
                    : styles['cut']
                }
              >
                {cutOut}mm
              </p>
            </DescriptionPart>
          </Description>
        </div>
        {hasPatentProtection && (
          <p className={styles['patent-protection']}>
            optical patent protection
          </p>
        )}

        <a href="#spec-code">
          <p className={styles['quick-spec-button']}>
            build your fitting{' '}
            <svg
              width="10"
              height="13"
              viewBox="0 0 10 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.23769 10.3778L5.50087 10.3024L8.66653 6.99952C9.3758 6.81165 9.66426 7.37845 9.24049 8.01751C8.60706 8.97288 6.5328 11.0217 5.64659 11.9C4.68901 12.8506 4.74849 12.8506 3.79091 11.9C2.89132 11.0073 0.849777 8.99055 0.197017 8.01751C-0.015613 7.69959 -0.128619 7.28372 0.241625 7.02681C0.589565 6.78435 0.840855 7.04929 1.10255 7.26284C1.93374 7.94365 2.95675 9.26833 3.75969 10.0952C3.92325 10.2638 3.86526 10.4613 4.19982 10.3778V0.258914C4.19982 -0.0863047 5.23769 -0.0863047 5.23769 0.258914V10.3762V10.3778Z"
                fill="white"
              />
            </svg>
          </p>
        </a>
      </div>
      <LitOptions className={styles.flex} pose={entered ? 'enter' : 'exit'}>
        <ul>
          {litEffects.map((item, index) => {
            return (
              <LitOption key={index}>
                <div className={styles['icons-wrapper']}>
                  <span className={iconClasses}>
                    {ICONS_PRODUCT_HERO[item.slug]}
                  </span>
                  <Tick className={styles['tick-icon']} />
                </div>
                <span className={styles.type}>{item.name}</span>
              </LitOption>
            );
          })}
        </ul>
        <p className={`${styles['lit-effect']} ${styles[theme]}`}>lit effect</p>
      </LitOptions>
    </div>
  );
};

ProductHero.propTypes = {
  theme: PropTypes.string,
  background: PropTypes.string.isRequired,
  title: PropTypes.string,
  litEffects: PropTypes.array,
  cutOut: PropTypes.string,
  allVariants: PropTypes.array,
  smallScreen: PropTypes.bool,
};
